/**
 * ChatTaskTerminalCard — final summary card emitted on terminal task state.
 *
 * Phase 3 stops at "build complete"; Phase 4 will overwrite the summary
 * text with the CR link.
 *
 * Phase 3 - Task 19.
 */

import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';

export type ChatTaskTerminalOutcome = 'success' | 'warning' | 'failure';

export interface ChatTaskTerminalCardProps {
  readonly runId: string;
  readonly title: string;
  readonly outcome: ChatTaskTerminalOutcome;
  readonly summary: string;
}

const ICON: Record<ChatTaskTerminalOutcome, string> = {
  success: '✓',
  warning: '!',
  failure: '×',
};

const COLOR: Record<ChatTaskTerminalOutcome, string> = {
  success: 'bg-emerald-600',
  warning: 'bg-amber-600',
  failure: 'bg-rose-600',
};

export function ChatTaskTerminalCard({
  runId,
  title,
  outcome,
  summary,
}: ChatTaskTerminalCardProps): ReactElement {
  return (
    <article
      data-runid={runId}
      data-outcome={outcome}
      className="flex items-center gap-3 rounded-lg border border-zinc-700/50 bg-zinc-900/40 p-3"
    >
      <span
        aria-hidden="true"
        className={`flex h-7 w-7 items-center justify-center rounded-full text-white ${COLOR[outcome]}`}
      >
        {ICON[outcome]}
      </span>
      <div className="flex-1 min-w-0">
        <h3 className="truncate font-medium text-zinc-100">{title}</h3>
        <p className="text-sm text-zinc-400">{summary}</p>
      </div>
      <Link
        to={`/tasks?runId=${runId}`}
        className="text-sm text-sky-400 hover:underline"
      >
        Open
      </Link>
    </article>
  );
}

export default ChatTaskTerminalCard;
